import { useCallback, useEffect, useState } from 'react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Textarea } from '../ui/textarea';
import ClinicalFormShell from './ClinicalFormShell';
import { mergeRehabilitaIntake } from '../../lib/clinicalRehabilitaSchema';
import { formatClinicalHistoryTitle } from '../../lib/clinicalFormTemplates';
import { DEFAULT_BRAND_NAME } from '../../lib/clinicSiteDefaults';
import { useClinicBranding } from '../../context/ClinicBrandingContext';
import { patchPatient, patchPatientRecord } from '../../lib/api';
import { toast } from 'sonner';
import { Save } from 'lucide-react';

function FieldRow({ label, hint, children }) {
  return (
    <div className="space-y-1.5">
      <Label className="text-sm font-semibold">{label}</Label>
      {hint ? <p className="text-xs text-muted-foreground">{hint}</p> : null}
      {children}
    </div>
  );
}

export default function RehabilitaIntakeForm({
  recordId,
  patientId,
  patient,
  initialIntake,
  recordStatus,
  onSaved,
  onCompleted,
}) {
  const isComplete = recordStatus === 'complete';
  const [intake, setIntake] = useState(() => mergeRehabilitaIntake(initialIntake));
  const [saving, setSaving] = useState(false);
  const { info: clinicInfo } = useClinicBranding();
  const formTitle = formatClinicalHistoryTitle(clinicInfo?.name, DEFAULT_BRAND_NAME);

  useEffect(() => {
    setIntake(mergeRehabilitaIntake(initialIntake));
  }, [initialIntake]);

  const update = useCallback((key, value) => {
    setIntake((prev) => ({ ...prev, [key]: value }));
  }, []);

  const saveDraft = async (markComplete = false) => {
    const patientName = String(patient?.full_name || '').trim();
    if (markComplete && !patientName) {
      toast.error('Indique el nombre del paciente en el expediente');
      return;
    }
    setSaving(true);
    try {
      if (patientId && intake.edad) {
        await patchPatient(patientId, {
          full_name: patientName,
        });
      }
      const payload = {
        intake_adult: mergeRehabilitaIntake({
          ...intake,
          header: {
            ...intake.header,
            form_date: intake.header?.form_date || new Date().toISOString().slice(0, 10),
          },
        }),
        status: markComplete ? 'complete' : recordStatus === 'complete' ? 'complete' : 'draft',
      };
      const saved = await patchPatientRecord(recordId, payload);
      toast.success(
        markComplete ? 'Historia clínica guardada' : isComplete ? 'Cambios guardados' : 'Borrador guardado',
      );
      onSaved?.(saved);
      if (markComplete) onCompleted?.(saved);
    } catch (e) {
      const d = e?.response?.data?.detail;
      toast.error(typeof d === 'string' ? d : 'No se pudo guardar');
    } finally {
      setSaving(false);
    }
  };

  const formDate = intake.header?.form_date || new Date().toISOString().slice(0, 10);

  return (
    <ClinicalFormShell
      title={formTitle}
      subtitle={intake.header?.folio ? `Folio ${intake.header.folio}` : undefined}
    >
      <div className="grid gap-4">
        <div className="flex flex-col sm:flex-row sm:items-end gap-4 sm:justify-between">
          <FieldRow label="Fecha">
            <Input
              type="date"
              className="h-11 max-w-[12rem]"
              value={formDate}
              onChange={(e) =>
                setIntake((prev) => ({
                  ...prev,
                  header: { ...prev.header, form_date: e.target.value },
                }))
              }
            />
          </FieldRow>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-[1fr_6rem] gap-4">
          <FieldRow label="Nombre">
            <Input
              className="h-11"
              value={patient?.full_name || ''}
              disabled
              placeholder="Del expediente del paciente"
            />
          </FieldRow>
          <FieldRow label="Edad">
            <Input
              className="h-11"
              inputMode="numeric"
              placeholder="Años"
              value={intake.edad}
              onChange={(e) => update('edad', e.target.value)}
            />
          </FieldRow>
        </div>

        <FieldRow label="Antecedentes patológicos">
          <Input className="h-11" value={intake.ap} onChange={(e) => update('ap', e.target.value)} />
        </FieldRow>

        <FieldRow label="Alergias">
          <Input className="h-11" value={intake.a} onChange={(e) => update('a', e.target.value)} />
        </FieldRow>

        <FieldRow label="Actividad física">
          <Input className="h-11" value={intake.af} onChange={(e) => update('af', e.target.value)} />
        </FieldRow>

        <FieldRow label="Actividad deportiva">
          <Input className="h-11" value={intake.ad} onChange={(e) => update('ad', e.target.value)} />
        </FieldRow>

        <FieldRow label="Medicamentos">
          <Input
            className="h-11"
            value={intake.medicamentos}
            onChange={(e) => update('medicamentos', e.target.value)}
          />
        </FieldRow>

        <FieldRow label="Rayos x">
          <Input className="h-11" value={intake.rx} onChange={(e) => update('rx', e.target.value)} />
        </FieldRow>

        <FieldRow label="Historia clínica">
          <Textarea
            rows={4}
            value={intake.historia}
            onChange={(e) => update('historia', e.target.value)}
            placeholder="Evolución, hallazgos, notas…"
          />
        </FieldRow>

        <FieldRow label="Diagnóstico">
          <Input className="h-11" value={intake.dx} onChange={(e) => update('dx', e.target.value)} />
        </FieldRow>

        <FieldRow label="Plan de tratamiento">
          <Textarea
            rows={4}
            value={intake.plan_tx}
            onChange={(e) => update('plan_tx', e.target.value)}
            placeholder="Objetivos, sesiones, indicaciones…"
          />
        </FieldRow>

        <div className="rounded-lg border border-dashed p-4 bg-muted/20 text-sm text-muted-foreground">
          En cada <strong>nota de seguimiento</strong> podrá imprimir la plantilla PDF. El nombre del médico y la
          cédula se toman de <strong>Settings → Historia clínica — médico responsable</strong>.
        </div>

        <div className="flex flex-wrap gap-2 pt-2 border-t border-border">
          <Button type="button" variant="outline" disabled={saving} onClick={() => void saveDraft(false)}>
            <Save className="w-4 h-4 mr-1" />
            {saving ? 'Guardando…' : isComplete ? 'Guardar cambios' : 'Guardar borrador'}
          </Button>
          {!isComplete ? (
            <Button type="button" disabled={saving} onClick={() => void saveDraft(true)}>
              {saving ? 'Guardando…' : 'Finalizar historia'}
            </Button>
          ) : null}
        </div>
      </div>
    </ClinicalFormShell>
  );
}
